/**
 * The chrome around a listicle, in five languages.
 *
 * One file rather than five, same reasoning as `tools/ui.ts` and
 * `compare/ui.ts`: a dozen short strings every page needs, and splitting them
 * per language would fetch five chunks to render one page.
 *
 * `bestForLabel` and `limitationLabel` are the two that carry the format. Every
 * entry on every list renders both, in that order, and the limitation is not
 * smaller or greyer than the strength — a listicle whose downsides are set in
 * footnote type is the shape readers have learned to distrust.
 */

import type { ContentLang } from "@fazly/shared/contentLang";

export interface BestUi {
  readonly disclosureLabel: string;
  readonly listHeading: string;
  readonly bestForLabel: string;
  readonly limitationLabel: string;
  readonly readComparison: string;
  readonly openTool: string;
  readonly pickHeading: string;
  readonly methodHeading: string;
  readonly faqHeading: string;
  readonly otherLists: string;
  readonly tryFirst: string;
  readonly toolsCta: string;
  readonly indexTitle: string;
  readonly indexDescription: string;
  readonly indexH1: string;
  readonly indexStandfirst: string;
}

export const BEST_UI: Record<ContentLang, BestUi> = {
  en: {
    disclosureLabel: "Who wrote this",
    listHeading: "The list",
    bestForLabel: "Best for",
    limitationLabel: "What it cannot do",
    readComparison: "read the full comparison",
    openTool: "open the free tool",
    pickHeading: "If you only take one thing from this page",
    methodHeading: "How this list was ordered",
    faqHeading: "Questions",
    otherLists: "Other lists",
    tryFirst: "Or skip the list and measure something — free, no account:",
    toolsCta: "open the free tools",
    indexTitle: "Best Face Apps — Honest Lists, Including Ours | Fazly",
    indexDescription:
      "Six honest lists of the best looksmaxxing, face analysis, face rating, face shape, jawline and mewing apps — written by one of them, and saying so.",
    indexH1: "The best of, honestly",
    indexStandfirst:
      "Six lists. We are on all of them and we are the pick on two, which is the whole point.",
  },
  tr: {
    disclosureLabel: "Bu sayfayı kim yazdı",
    listHeading: "Liste",
    bestForLabel: "Şuna uygun",
    limitationLabel: "Yapamadığı şey",
    readComparison: "tam karşılaştırmayı oku",
    openTool: "ücretsiz aracı aç",
    pickHeading: "Bu sayfadan tek bir şey alacaksan",
    methodHeading: "Bu liste neye göre sıralandı",
    faqHeading: "Sorular",
    otherLists: "Diğer listeler",
    tryFirst: "Ya da listeyi atla ve bir şey ölç — ücretsiz, hesap gerekmiyor:",
    toolsCta: "ücretsiz araçları aç",
    indexTitle: "En İyi Yüz Uygulamaları — Dürüst Listeler | Fazly",
    indexDescription:
      "En iyi looksmaxxing, yüz analizi, yüz puanlama, yüz şekli, çene hattı ve mewing uygulamaları için altı dürüst liste — biri tarafından yazıldı ve bunu söylüyor.",
    indexH1: "En iyiler, dürüstçe",
    indexStandfirst:
      "Altı liste. Hepsinde varız ve sadece ikisinde seçilen biziz — mesele tam olarak bu.",
  },
  de: {
    disclosureLabel: "Wer das geschrieben hat",
    listHeading: "Die Liste",
    bestForLabel: "Gut für",
    limitationLabel: "Was es nicht kann",
    readComparison: "vollständigen vergleich lesen",
    openTool: "kostenloses werkzeug öffnen",
    pickHeading: "Wenn du nur eines mitnimmst",
    methodHeading: "Wonach diese Liste sortiert ist",
    faqHeading: "Fragen",
    otherLists: "Weitere Listen",
    tryFirst:
      "Oder überspring die Liste und miss etwas — kostenlos, ohne Konto:",
    toolsCta: "kostenlose werkzeuge öffnen",
    indexTitle: "Beste Gesichts-Apps — Ehrliche Listen | Fazly",
    indexDescription:
      "Sechs ehrliche Listen der besten Looksmaxxing-, Gesichtsanalyse-, Gesichtsbewertungs-, Gesichtsform-, Kieferlinien- und Mewing-Apps — geschrieben von einer davon.",
    indexH1: "Die besten, ehrlich",
    indexStandfirst:
      "Sechs Listen. Wir stehen auf allen und sind bei zweien die Empfehlung — genau darum geht es.",
  },
  es: {
    disclosureLabel: "Quién escribió esto",
    listHeading: "La lista",
    bestForLabel: "Ideal para",
    limitationLabel: "Lo que no puede hacer",
    readComparison: "leer la comparación completa",
    openTool: "abrir la herramienta gratis",
    pickHeading: "Si solo te llevas una cosa",
    methodHeading: "Cómo se ordenó esta lista",
    faqHeading: "Preguntas",
    otherLists: "Otras listas",
    tryFirst: "O sáltate la lista y mide algo: gratis y sin cuenta.",
    toolsCta: "abrir las herramientas gratis",
    indexTitle: "Mejores Apps de Cara — Listas Honestas | Fazly",
    indexDescription:
      "Seis listas honestas de las mejores apps de looksmaxxing, análisis facial, puntuación de cara, forma de cara, mandíbula y mewing — escritas por una de ellas.",
    indexH1: "Las mejores, con honestidad",
    indexStandfirst:
      "Seis listas. Estamos en todas y somos la recomendación en dos, que es justo el punto.",
  },
  pt: {
    disclosureLabel: "Quem escreveu isto",
    listHeading: "A lista",
    bestForLabel: "Melhor para",
    limitationLabel: "O que não consegue fazer",
    readComparison: "ler a comparação completa",
    openTool: "abrir a ferramenta grátis",
    pickHeading: "Se você levar só uma coisa daqui",
    methodHeading: "Como esta lista foi ordenada",
    faqHeading: "Perguntas",
    otherLists: "Outras listas",
    tryFirst: "Ou pule a lista e meça alguma coisa — grátis, sem conta:",
    toolsCta: "abrir as ferramentas grátis",
    indexTitle: "Melhores Apps de Rosto — Listas Honestas | Fazly",
    indexDescription:
      "Seis listas honestas dos melhores apps de looksmaxxing, análise facial, nota de rosto, formato de rosto, mandíbula e mewing — escritas por um deles.",
    indexH1: "Os melhores, com honestidade",
    indexStandfirst:
      "Seis listas. Estamos em todas e somos a escolha em duas — é exatamente esse o ponto.",
  },
};
